/**
 * Supplier-facing deadline wording. Pure so it can be tested without rendering.
 *
 * - approved: no urgency (null).
 * - delivered (in review): neutral date, no countdown.
 * - open: countdown that escalates to warning (today/tomorrow) and danger (past).
 */
export type DeadlineTone = 'neutral' | 'warning' | 'danger';
export type DeadlineState = 'open' | 'delivered' | 'approved';

export interface DeadlineChip {
  label: string;
  tone: DeadlineTone;
  /** Full date for tooltips / screen readers. */
  title: string;
  /** Calendar days from today to the deadline (negative once past). */
  daysLeft: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDateOnly = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateOnly = (value: string) => value.split('-').reverse().join('/');

/** Calendar-day difference between a yyyy-MM-dd deadline and `today` (local time). */
export function daysUntil(deadline: string, today: Date = new Date()): number | null {
  const due = parseDateOnly(deadline);
  if (!due) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((due.getTime() - start.getTime()) / DAY_MS);
}

export function getDeadlineChip(
  deadline: string | null | undefined,
  state: DeadlineState,
  today: Date = new Date(),
): DeadlineChip | null {
  if (!deadline || state === 'approved') return null;
  const days = daysUntil(deadline, today);
  if (days === null) return null;
  const title = `Fecha límite: ${formatDateOnly(deadline)}`;
  if (state === 'delivered') {
    return { label: `Fecha límite ${formatDateOnly(deadline)}`, tone: 'neutral', title, daysLeft: days };
  }
  if (days < 0) {
    const ago = -days;
    return { label: `Vencido hace ${ago} ${ago === 1 ? 'día' : 'días'}`, tone: 'danger', title, daysLeft: days };
  }
  if (days === 0) return { label: 'Vence hoy', tone: 'warning', title, daysLeft: days };
  if (days === 1) return { label: 'Vence mañana', tone: 'warning', title, daysLeft: days };
  return { label: `Vence en ${days} días`, tone: 'neutral', title, daysLeft: days };
}
